import {
  Box,
  Button,
  Flex,
  FormLabel,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Link, useParams } from 'react-router-dom';
import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import PassesRenderStatus from '../PassesRenderStatus';
import InfoBanner from '../../../common/InfoBanner';
import { CustomInfoIcon } from '../../../../theme/icons/CustomInfoIcon';
import { CustomArrowRightIcon } from '../../../../theme/icons/CustomArrowRightIcon';
import DeactivatePassModal from '../modals/DeactivatePassModal';
import CustomToast from '../../../../common/CustomToast';
import { useDeactivatePassMutation } from '../../../../store/redux/api/passApi';
import type { PassAttributes } from '../../../../store/types/pass';
import { getTimezoneAbbr } from '../utils/passHelpers';

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatTimestamp = (
  dateStr: string | null | undefined,
  fallback = 'n/a'
): string => {
  if (!dateStr) return fallback;
  const timestamp = new Date(dateStr);
  const day = timestamp.getDate();
  const month = timestamp.toLocaleString('en-GB', { month: 'short' });
  const year = String(timestamp.getFullYear()).slice(-2);
  const hours = timestamp.getHours();
  const minutes = String(timestamp.getMinutes()).padStart(2, '0');
  const tzAbbr = getTimezoneAbbr(LOCAL_TZ, timestamp);
  return `${month} ${day} ${year}, ${hours}.${minutes} ${tzAbbr}`;
};

const STATUS_TIMESTAMPS: Record<
  string,
  { label: string; key: keyof PassAttributes }
> = {
  issued: { label: 'This pass was issued on', key: 'createdAt' },
  active: { label: 'Active since', key: 'addedAt' },
  expired: { label: 'This pass expired on', key: 'expiresAt' },
  deactivated: { label: 'This pass was deactivated on', key: 'deactivatedAt' },
  removed: { label: 'This pass was removed on', key: 'removedAt' },
};

interface PassManagementSectionProps {
  attributes?: PassAttributes;
}

const PassManagementSection = ({ attributes }: PassManagementSectionProps) => {
  const { uuid } = useParams<{ uuid: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [deactivatePass, { isLoading: isDeactivating }] =
    useDeactivatePassMutation();
  const toast = useToast();
  const isExpired = attributes?.expiresAt
    ? new Date(attributes.expiresAt) < new Date()
    : false;
  const status = isExpired ? 'expired' : attributes?.status;
  const timestampConfig = status ? STATUS_TIMESTAMPS[status] : null;
  const timestampValue = timestampConfig
    ? status === 'expired'
      ? attributes?.expiresAt
      : (attributes?.[timestampConfig.key] as string | null | undefined)
    : null;

  return (
    <SectionWrapper
      title="Pass management"
      subtitle={
        <Text fontSize="15px" color="grey">
            Control whether this pass can be scanned and whether it remains
            active.
        </Text>
      }
      openOnLoad
      id="pass-management-section"
    >
      <Box>
        <FormLabel variant="templateBuilderLabel" mb="8px">
          Status
        </FormLabel>
        <PassesRenderStatus value={status} hasTooltip />
        {attributes?.expiresAt &&
        !isExpired &&
        attributes?.status !== 'deactivated' ? (
          <Text fontSize="12px" color="#989898" mt="8px">
            This pass expires on {formatTimestamp(attributes.expiresAt)}
          </Text>
        ) : (
          timestampConfig && (
            <Text fontSize="12px" color="#989898" mt="8px">
              {timestampConfig.label} {formatTimestamp(timestampValue)}
            </Text>
          )
        )}
        {attributes?.status !== 'deactivated' && (
          <>
            <FormLabel variant="templateBuilderLabel" mt="24px">
              Pass controls
            </FormLabel>
            <InfoBanner
              color="#1868DB"
              bg="#E5F6FB"
              icon={<CustomInfoIcon boxSize="20px" color="#009BD6" />}
              borderRadius="5px"
              hasLeftBorder={false}
            >
              <Text textStyle="bodyFamilyRegular" fontSize="13px">
                Passes in wallets are a marketing channel - keeping this pass
                active lets you send updates and push notifications
              </Text>
            </InfoBanner>
            <Button
              variant="outlineDanger"
              size="sm"
              mt="24px"
              onClick={onOpen}
            >
              Deactivate pass
            </Button>
            <DeactivatePassModal
              isOpen={isOpen}
              onClose={onClose}
              isLoading={isDeactivating}
              onConfirm={async () => {
                if (!uuid) return;
                try {
                  await deactivatePass({ uuid }).unwrap();
                  onClose();
                  toast({
                    render: (props) => (
                      <CustomToast
                        status="success"
                        title="Pass deactivated"
                        description=""
                        onClose={props.onClose}
                        position="top"
                        duration={2000}
                        id={props.id}
                      />
                    ),
                  });
                } catch {
                  toast({
                    render: (props) => (
                      <CustomToast
                        status="error"
                        title="Failed to deactivate pass"
                        description="Please try again"
                        onClose={props.onClose}
                      />
                    ),
                  });
                }
              }}
            />
            {(attributes?.barcode?.enabled || !!attributes?.nfc) && (
              <Text
                textStyle="bodyFamilyRegular"
                fontSize="12px"
                color="#4C4C4C"
                mt="24px"
              >
                Want to stop entry without deactivating?{' '}
                <Link to={`/passes/edit/${uuid}#nfc-and-barcode-section`}>
                  <Text as="span" color="#009BD6">
                    Disable scanning instead
                  </Text>
                </Link>{' '}
                <CustomArrowRightIcon
                  boxSize="10px"
                  color="#009BD6"
                  display="inline"
                  verticalAlign="middle"
                />
              </Text>
            )}
          </>
        )}
      </Box>
    </SectionWrapper>
  );
};

export default PassManagementSection;
